import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, CampaignLimits } from 'api/types/user';
import { FeatureFlags } from 'api/types/featureFlags';
import { getStoredImpersonation, type ImpersonatedUserInfo } from './impersonationStorage';

export type { ImpersonatedUserInfo } from './impersonationStorage';
export { clearImpersonationStorage, syncImpersonationToStorage } from './impersonationStorage';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  featureFlags?: FeatureFlags;
  campaignLimits?: CampaignLimits | null;
  crispToken: string | null;
  impersonatedUserId: string | null;
  impersonatedUser: ImpersonatedUserInfo | null;
}

const stored = getStoredImpersonation();

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  featureFlags: undefined,
  campaignLimits: null,
  crispToken: null,
  impersonatedUserId: stored.impersonatedUserId,
  impersonatedUser: stored.impersonatedUser
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthState: (state, action: PayloadAction<Partial<AuthState>>) => {
      if (action.payload.isAuthenticated !== undefined)
        state.isAuthenticated = action.payload.isAuthenticated;
      if (action.payload.isLoading !== undefined) state.isLoading = action.payload.isLoading;
      if (action.payload.user !== undefined) {
        const previousUser = state.user;
        const userIdChanged = action.payload.user?.id !== previousUser?.id;
        state.user = action.payload.user;
        if (userIdChanged) {
          state.featureFlags = undefined;
          state.campaignLimits = null;
          state.crispToken = null;
          // Only clear impersonation when switching from one real user to another,
          // not when hydrating from null (initial load) to preserve sessionStorage state
          if (previousUser !== null) {
            state.impersonatedUserId = null;
            state.impersonatedUser = null;
          }
        }
      }
      if (action.payload.featureFlags !== undefined)
        state.featureFlags = action.payload.featureFlags;
      if (action.payload.campaignLimits !== undefined)
        state.campaignLimits = action.payload.campaignLimits;
      if (action.payload.crispToken !== undefined) state.crispToken = action.payload.crispToken;
      if (action.payload.impersonatedUserId !== undefined)
        state.impersonatedUserId = action.payload.impersonatedUserId ?? null;
      if (action.payload.impersonatedUser !== undefined)
        state.impersonatedUser = action.payload.impersonatedUser ?? null;
    },
    setImpersonatedUser: (state, action: PayloadAction<ImpersonatedUserInfo | null>) => {
      if (action.payload) {
        state.impersonatedUserId = action.payload.id;
        state.impersonatedUser = action.payload;
      } else {
        state.impersonatedUserId = null;
        state.impersonatedUser = null;
      }
    },
    clearImpersonation: (state) => {
      state.impersonatedUserId = null;
      state.impersonatedUser = null;
    }
  }
});

export const { setAuthState, setImpersonatedUser, clearImpersonation } = authSlice.actions;
export default authSlice.reducer;
